
import React, { useRef } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";
import BreatheEV_Logo from "../../Images/logo.png";
import { Image } from "antd";
import styles from "./styles.module.css";
import invoiceStyle from "./invoiceStyle.module.css";

const InvoiceSettledView = ({ bidData, selectedPaymentTermForInvoice }) => {
  let termsAndConditions = `Payment due NET30 upon receipt of final invoice.
                              Late payments are subject to a late payment fee of
                              1.5% per month. 

                              Any discrepancies must be reported within 7 days.`;

  const printRef = useRef();
  const headerRef = useRef(null);
  const footerRef = useRef(null);

  const getTableBodyData = () => {
    const tableBody = [];
    const rowMeta = [];
    const allItems = [];

    // 1. Helper to collect standard items
    const collectStandard = (data) => {
      if (data?.quantity) {
        allItems.push({
          ...data,
          // Ensure standard items have a default orderNumber if missing
          orderNumber: data.orderNumber ?? 0
        });
      }
    };

    // 2. Push all potential objects into the collection array
    collectStandard(newBidData?.l2Charger);
    collectStandard(newBidData?.l3Charger);
    collectStandard(newBidData?.electricalWork);
    collectStandard(newBidData?.wifiConnectivity);
    collectStandard(newBidData?.wifiAntenna);
    collectStandard(newBidData?.warranty);
    collectStandard(newBidData?.signage);
    collectStandard(newBidData?.pedestals);
    collectStandard(newBidData?.simCard4G);
    collectStandard(newBidData?.siteWiFi);
    collectStandard(newBidData?.chargerInstallOnly);
    collectStandard(newBidData?.shipping);

    // 3. Normalize and push custom data
    if (newBidData?.bidCustomData?.length > 0) {
      newBidData.bidCustomData.forEach((item) => {
        if (item?.fieldQuantity) {
          allItems.push({
            title: item.fieldTitle || "Custom Item",
            description: item.fieldDescription || "",
            pricePerUnit: item.fieldPricePerUnit,
            quantity: item.fieldQuantity,
            margin: item.fieldMargin || 0,
            orderNumber: item.orderNumber ?? 0
          });
        }
      });
    }

    // 4. SORT based on orderNumber
    allItems.sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0));

    // 5. Build tableBody and rowMeta from sorted list
    allItems.forEach((data) => {
      const rateValue = ((data.pricePerUnit * data.quantity) + (data.margin || 0)) / data.quantity;
      const amountValue = (data.quantity * data.pricePerUnit) + (data.margin || 0);

      const rate = `$ ${rateValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
      const amount = `$ ${amountValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

      tableBody.push([
        (data.title || "Item") + "\n" + (data.description || ""),
        rate,
        data.quantity.toString(),
        amount
      ]);

      rowMeta.push({
        title: data.title || "Item",
        description: data.description || ""
      });
    });

    return { tableBody, rowMeta };
  };

  // const getTableBodyData = () => {
  //   const tableBody = [];
  //   const rowMeta = [];

  //   const addDataRow = (data) => {
  //     if (data?.quantity) {
  //       const rate = `$ ${(((data.pricePerUnit * data.quantity) + data.margin) / data.quantity).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  //       const amount = `$ ${((data.quantity * data.pricePerUnit) + data.margin).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  //       tableBody.push([
  //         (data.title || "Item") + "\n" + (data.description || ""),
  //         rate,
  //         data.quantity.toString(),
  //         amount
  //       ]);
  //       rowMeta.push({ title: data.title || "Item", description: data.description || "" });
  //     }
  //   };

  //   addDataRow(newBidData?.l2Charger);
  //   addDataRow(newBidData?.l3Charger);
  //   addDataRow(newBidData?.electricalWork);
  //   addDataRow(newBidData?.wifiConnectivity);
  //   addDataRow(newBidData?.wifiAntenna);
  //   addDataRow(newBidData?.warranty);
  //   addDataRow(newBidData?.signage);
  //   addDataRow(newBidData?.pedestals);
  //   addDataRow(newBidData?.simCard4G);
  //   addDataRow(newBidData?.siteWiFi);
  //   addDataRow(newBidData?.chargerInstallOnly);

  //   if (newBidData?.bidCustomData?.length > 0) {
  //     newBidData.bidCustomData.forEach((item) => {
  //       if (item?.fieldQuantity && item?.fieldPricePerUnit) {
  //         const rate = `$ ${(((item.fieldPricePerUnit * item.fieldQuantity) + item.fieldMargin) / item.fieldQuantity).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  //         const amount = `$ ${((item.fieldQuantity * item.fieldPricePerUnit) + item.fieldMargin).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;
  //         tableBody.push([
  //           (item.fieldTitle || "Custom Item") + "\n" + (item.fieldDescription || ""),
  //           rate,
  //           item.fieldQuantity.toString(),
  //           amount
  //         ]);
  //         rowMeta.push({ title: item.fieldTitle || "Custom Item", description: item.fieldDescription || "" });
  //       }
  //     });
  //   }

  //   addDataRow(newBidData?.shipping);
  //   return { tableBody, rowMeta };
  // };

  const downloadPDF = async () => {
    const pdf = new jsPDF("p", "pt", "letter");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const marginPt = 10 * 2.83465;
    const renderWidth = pdfWidth - (marginPt * 2);

    const headerEl = headerRef.current;
    if (!headerEl) return;
    const headerCanvas = await html2canvas(headerEl, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
    const headerImg = headerCanvas.toDataURL("image/png", 1.0);
    const headerRenderHeight = (headerCanvas.height * renderWidth) / headerCanvas.width;

    pdf.addImage(headerImg, "PNG", marginPt, marginPt, renderWidth, headerRenderHeight);

    let finalY = marginPt + headerRenderHeight + 20;

    const { tableBody, rowMeta } = getTableBodyData();

    autoTable(pdf, {
      startY: finalY,
      head: [['ITEMS & DESCRIPTION', 'RATE', 'QTY', 'AMOUNT']],
      body: tableBody,
      margin: { left: marginPt, right: marginPt },
      theme: 'grid',
      headStyles: { fillColor: [233, 234, 235], textColor: 0, fontSize: 10, fontStyle: 'bold' },
      bodyStyles: { textColor: 0, fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 'auto' },
        1: { halign: 'right', cellWidth: 80, fontStyle: 'bold' },
        2: { halign: 'right', cellWidth: 40, fontStyle: 'bold' },
        3: { halign: 'right', cellWidth: 80, fontStyle: 'bold' }
      },
      styles: { lineColor: [153, 153, 153], lineWidth: 0.5, cellPadding: 6, font: 'helvetica', overflow: 'linebreak' },
      rowPageBreak: 'avoid',
      willDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 0) {
          data.cell.text = [];
        }
      },
      didDrawCell: (data) => {
        if (data.section === 'body' && data.column.index === 0) {
          const meta = rowMeta[data.row.index];
          if (!meta) return;

          const pad = 6;
          const fontSize = 10;
          const lineHeight = fontSize * 1.15;
          const textWidth = data.cell.width - pad * 2;
          const x = data.cell.x + pad;
          let y = data.cell.y + pad + fontSize * 0.75;

          pdf.setFontSize(fontSize);
          pdf.setTextColor(0, 0, 0);

          pdf.setFont('helvetica', 'bold');
          const titleLines = pdf.splitTextToSize(meta.title, textWidth);
          pdf.text(titleLines, x, y);
          y += titleLines.length * lineHeight;

          if (meta.description) {
            pdf.setFont('helvetica', 'normal');
            const descLines = pdf.splitTextToSize(meta.description, textWidth);
            pdf.text(descLines, x, y);
          }
        }
      }
    });

    finalY = pdf.lastAutoTable.finalY + 20;

    const footerEl = footerRef.current;
    if (footerEl) {
      const footerCanvas = await html2canvas(footerEl, { scale: 2, useCORS: true, backgroundColor: "#ffffff" });
      const footerImg = footerCanvas.toDataURL("image/png", 1.0);
      const footerRenderHeight = (footerCanvas.height * renderWidth) / footerCanvas.width;

      if (finalY + footerRenderHeight > pdfHeight - marginPt) {
        pdf.addPage();
        finalY = marginPt;
      }

      pdf.addImage(footerImg, "PNG", marginPt, finalY, renderWidth, footerRenderHeight);
    }

    pdf.save(`Invoice-${bidData.bidNumber}.pdf`);
  };

  const newBidData = bidData;

  const rows = [];

  // predefined fields
  const addRow = (data) => {
    if (data?.quantity) {
      rows.push(
        <tr key={data.title}>
          <td>
            <span className={styles["table-item-title"]}>
              <b>{data.title}</b>
            </span>
            <br></br>
            <span className={styles["table-item-description"]}>
              {data.description}
            </span>
          </td>
          <td className={styles['table-items-right']}>
            <b>$ {(((data.pricePerUnit * data.quantity) + data.margin) / data.quantity).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</b>
          </td>
          <td className={styles['table-items-right']}>
            <b>{data.quantity}</b>
          </td>
          <td className={styles['table-items-right']}>
            <b>$ {((data.quantity * data.pricePerUnit) + data.margin).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</b>
          </td>
        </tr>
      );
    }
  };


  const lineItems = [
    newBidData?.l2Charger,
    newBidData?.l3Charger,
    newBidData?.electricalWork,
    newBidData?.wifiConnectivity,
    newBidData?.wifiAntenna,
    newBidData?.warranty,
    newBidData?.signage,
    newBidData?.pedestals,
    newBidData?.simCard4G,
    newBidData?.siteWiFi,
    newBidData?.chargerInstallOnly,
    newBidData?.shipping
  ].filter(item => item?.quantity);



  if (newBidData?.bidCustomData?.length > 0) {
    newBidData.bidCustomData.forEach((item) => {
      if (item?.fieldQuantity) {
        lineItems.push({
          title: item.fieldTitle || "Custom Item",
          description: item.fieldDescription,
          pricePerUnit: item.fieldPricePerUnit,
          quantity: item.fieldQuantity,
          margin: item.fieldMargin,
          orderNumber: item.orderNumber || 0 // Ensure custom fields have an orderNumber
        });
      }
    });
  }

  lineItems.sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0));

  lineItems.forEach((data, index) => {
    rows.push(
      <tr key={data.title + index}>
        <td>
          <b>{data.title}</b> <br />
          {data.description}
        </td>
        <td className={styles['table-items-right']}>
          $ {(((data.pricePerUnit * data.quantity) + data.margin) / data.quantity)
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        </td>
        <td className={styles['table-items-right']}>{data.quantity}</td>
        <td className={styles['table-items-right']}>
          $ {((data.quantity * data.pricePerUnit) + data.margin)
            .toFixed(2)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
        </td>
      </tr>
    );
  });


  // addRow(newBidData?.l2Charger);
  // addRow(newBidData?.l3Charger);
  // addRow(newBidData?.electricalWork);
  // addRow(newBidData?.wifiConnectivity);
  // addRow(newBidData?.wifiAntenna);
  // addRow(newBidData?.warranty);
  // addRow(newBidData?.signage);
  // addRow(newBidData?.pedestals);
  // addRow(newBidData?.simCard4G);
  //   addRow(newBidData?.siteWiFi);
  //   addRow(newBidData?.chargerInstallOnly);

  // // custom fields
  // if (newBidData?.bidCustomData?.length > 0) {
  //   newBidData.bidCustomData.forEach((item, index) => {
  //     if (item?.fieldQuantity && item?.fieldPricePerUnit) {
  //       rows.push(
  //         <tr key={`custom-${index}`}>
  //           <td>
  //             <span className={styles["table-item-title"]}> 
  //               <b>{item.fieldTitle || "Custom Item"}</b>
  //             </span>
  //             <br></br>
  //             <span className={styles["table-item-description"]}>
  //               {item.fieldDescription}
  //             </span>
  //           </td>
  //           <td className={styles['table-items-right']}>
  //             <b>$ {(((item.fieldPricePerUnit * item.fieldQuantity) + item.fieldMargin) / item.fieldQuantity).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</b>
  //           </td>
  //           <td className={styles['table-items-right']}>
  //             <b>{item.fieldQuantity}</b>
  //           </td>
  //           <td className={styles['table-items-right']}>
  //             <b>$ {((item.fieldQuantity * item.fieldPricePerUnit) + item.fieldMargin).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</b>
  //           </td>
  //         </tr>
  //       );
  //     }
  //   });
  // }

  // addRow(newBidData?.shipping);

  const getEmailFontSize = (email = '') => {
    const len = email.length;
    if (len >= 44) return '10px';
    if (len >= 40) return '11px';
    if (len > 38) return '12px';
    if (len > 32) return '12px';
    if (len > 26) return '13px';
    if (len > 20) return '14px';
    return '14px';
  };

  function formatUSPhoneNumber(phoneNumberString) {
    var cleaned = ('' + phoneNumberString).replace(/\D/g, '');
    var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return '(' + match[1] + ') ' + match[2] + '-' + match[3];
    }
    return null;
  }


  return (
    <>
      <div
        ref={printRef}
        // className={styles["print-content"]}
        style={{
          padding: "20px",
          backgroundColor: "white",
          color: "black",
          width: "794px",
          fontFamily: "Montserrat, sans-serif",
        }}
      >
        <div ref={headerRef} style={{ backgroundColor: 'white' }}>
          <div className={styles['top-margin']}></div>
          <div className={styles['pdf-header']}>
            <div className={styles['pdf-header-logo']}>
              <Image className={styles['pdf-image']} src={BreatheEV_Logo} alt="BreatheEV Logo" />
              <div className={styles['pdf-header-proposal-number']}>
                <b>INVOICE</b><br />
                IN-{bidData.bidNumber}
              </div>
            </div>
            <div className={styles["pdf-contact"]}>
              <h2>BreatheEV</h2>
              {bidData.name} <br></br>
              {bidData.email} <br></br>
              {formatUSPhoneNumber(bidData.phone)}
              <br></br>
              <br></br>
              <p>
                <b>Date</b> <br></br>
                {new Date(bidData.dateCreated).toLocaleDateString()}
              </p>
            </div>

            <div className={styles['pdf-header-prepared-for']}>
              <p>PREPARED FOR</p>
              {
                <>
                  {bidData.dealId?.dealname ? bidData.dealId?.dealname : <></>} <br></br>
                  {bidData.dealId?.property_address ? bidData.dealId?.property_address : <></>} <br></br> <br></br>
                  {bidData.dealPOC?.name ? bidData.dealPOC?.name : <></>} <br></br>
                  {bidData.dealPOC?.email
                    ? <span className={styles['pdf-poc-email']} style={{ fontSize: getEmailFontSize(bidData.dealPOC.email) }}>{bidData.dealPOC.email}</span>
                    : <></>}
                  {bidData.dealPOC?.phone ? bidData.dealPOC?.phone : <></>}
                </>
              }
            </div>
          </div>
        </div>
        {/* <div id="pdf-header-wrapper" ref={headerRef} style={{ backgroundColor: 'white' }}>
          <div className={styles["top-margin"]}></div>
          <div className={styles["pdf-header"]}>
            <Image
              className={styles["pdf-image"]}
              src={BreatheEV_Logo}
              alt="BreatheEV Logo"
            />
            <div className={styles["pdf-contact"]}>
              <h3>BreatheEV</h3>
              {bidData.name} <br></br>
              {bidData.email} <br></br>
              {bidData.phone}
            </div>

            <div>
              <p>PREPARED FOR</p>
              <br></br>
              {
                // bidData.dealPOC ?
                //   <>
                //     {bidData.dealPOC.email} <br></br>
                //     {bidData.dealPOC.phone}
                //   </> :
                //   bidData.dealId?.address
                <>
                  {bidData.dealId?.dealname ? bidData.dealId?.dealname : <></>} <br></br>
                  {bidData.dealId?.property_address ? bidData.dealId?.property_address : <></>} <br></br> <br></br>
                  {bidData.dealPOC.name ? bidData.dealPOC.name : <></>} <br></br>
                  {bidData.dealPOC.email ? bidData.dealPOC.email : <></>} <br></br>
                  {bidData.dealPOC.phone ? bidData.dealPOC.phone : <></>}
                </>
              }
            </div>
          </div>

          <div className={styles["pdf-sub-header"]}>
            <p>
              <b>INVOICE</b> <br></br>
              IN-{bidData.bidNumber}
            </p>
            <p>
              <b>Date</b> <br></br>
              {new Date().toLocaleDateString()}
            </p>
          </div>
        </div> */}
        <table
          border="1"
          cellPadding="6"
          className={styles["table"]}
        // style={{ borderCollapse: "collapse", width: "100%" }}
        >
          <thead>
            <tr>
              <th>ITEMS & DESCRIPTION</th>
              <th>RATE</th>
              <th>QTY</th>
              <th>AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            {rows.length > 0 ? (
              rows
            ) : (
              <tr>
                <td colSpan="4">No items available</td>
              </tr>
            )}
          </tbody>
        </table>
        <div id="pdf-footer-wrapper" ref={footerRef} style={{ backgroundColor: 'white' }}>
          <div className={invoiceStyle["cost-div-settled"]}>
            <div>
              <p>
                SUBTOTAL:
                <span className={styles["pdf-footer-values"]}>
                  <b className={invoiceStyle['invoice-value']}>$ {bidData.subTotal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</b>
                </span>
              </p>
              <p>
                {`SALES TAX (${bidData.salesTaxPercent ? `${!Number.isInteger(bidData.salesTaxPercent) ? Number(bidData.salesTaxPercent).toFixed(2) : bidData.salesTaxPercent}%` : '$'}):`}
                <span className={styles["pdf-footer-values"]}>
                  <b>
                    <b>$ {bidData.salesTax.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}</b>
                  </b>
                </span>
              </p>

              <hr></hr>
              <p className={styles["pdf-total-cost"]}>
                <b>Amount Due </b>
                <span className={styles["pdf-footer-values"]}>
                  <b>
                    <b>{(bidData.paymentType === "PartPayment" && selectedPaymentTermForInvoice == 1) ? `$ ${((bidData.subTotal + bidData.salesTax) / 2).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}` : `$ 0.00`}</b>
                  </b>
                </span>
              </p>
            </div>
            <div className={invoiceStyle['paid-in-full']}>
              <span className={invoiceStyle['payment-date-display-header']}>{(bidData.paymentType === "PartPayment" && selectedPaymentTermForInvoice == 1) ? "PARTIAL PAYMENT" : 'PAID IN FULL'}</span>
              <span className={invoiceStyle['payment-date-display-value']}>Settled on: {(bidData.paymentType === "PartPayment" && selectedPaymentTermForInvoice == 1) ? new Date(bidData?.paymentTerms[0]?.date).toLocaleDateString("en-US") : new Date(bidData?.paymentTerms[bidData?.paymentTerms.length - 1]?.date).toLocaleDateString("en-US")}</span>
            </div>
            {/* <div>
            <div className={invoiceStyle["signature-div"]}>
            <p>Signature: _______________________</p>
            <p>Name: __________________________ </p>
            <p>Date: ____________________________</p>
          </div>
          </div> */}
          </div>
          {/* <div className={invoiceStyle["terms-parent-div"]}>
          
          
        </div> */}
        </div>
      </div>
      <button
        disabled={!(bidData.bidStatus === "APPROVED" && (bidData.dealId?.dealstage !== "Bid Sent" && bidData.dealId?.dealstage !== "Pending Bid"))}
        onClick={downloadPDF}
        style={!(bidData.bidStatus === "APPROVED" && (bidData.dealId?.dealstage !== "Bid Sent" && bidData.dealId?.dealstage !== "Pending Bid")) ? {
          marginTop: "20px",
          padding: "10px 15px",
          background: "#ccc",
          color: "white",
          border: "none",
          borderRadius: "8px",
          cursor: "not-allowed",
        } : {
          marginTop: "20px",
          padding: "10px 15px",
          background: bidData.bidStatus === "APPROVED" ? "#FF4040" : "#ccc",
          color: "white",
          border: "none",
          borderRadius: "8px",
          cursor: bidData.bidStatus === "APPROVED" ? "pointer" : "not-allowed",
        }}
      >
        Download Settled Invoice
      </button>
    </>
  );
};

export default InvoiceSettledView;
